import React, { ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { defineMessages, useIntl } from "react-intl";

import LeadFinderSubscriptionPlan from "@mapmycustomers/shared/enum/LeadFinderSubscriptionPlan";
import { Alert, Button } from "@mapmycustomers/ui";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import { startLeadFinderSearch } from "@app/component/leadFinder/store/actions";
import { getLeadFinderSearch } from "@app/component/leadFinder/store/selectors";
import { getLeadGenPlan, isCurrentUserOwner } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import MapViewportState from "@app/types/map/MapViewportState";
import useAnalytics from "@app/util/contexts/useAnalytics";
import showLeadFinderLimitAlert from "@app/util/leadFinder/showLeadFinderLimitAlert";
import showLeadFinderUpsell from "@app/util/leadFinder/showLeadFinderUpsell";

import History from "./components/History";
import SearchArea from "./components/SearchArea";
import SearchBox from "./components/SearchBox";
import styles from "./SearchPopover.module.scss";

interface Props {
  changeQuery?: boolean;
  defaultQuery?: string;
  isOwner: boolean;
  leadGenPlan?: LeadFinderSubscriptionPlan;
  onBeforeSearch?: () => void;
  onSearchSuccess?: (bounds?: google.maps.LatLngBounds) => void;
  query?: string;
  showQueryWarning?: boolean;
  startLeadFinderSearch: typeof startLeadFinderSearch.request;
  viewport?: MapViewportState;
}

const messages = defineMessages({
  queryWarning: {
    id: "leadFinder.searchPopover.queryWarning",
    defaultMessage:
      "Changing the search term above will clear previous results and any selected leads. Make sure to <b>add any desired Leads from the current selection</b> before changing your search.",
    description: "Lead Finder popover query change warning",
  },
  title: {
    id: "leadFinder.searchPopover.title",
    defaultMessage: "Lead Finder",
    description: "Lead Finder popover title",
  },
  upgrade: {
    id: "leadFinder.searchPopover.upgrade",
    defaultMessage: "Upgrade for more monthly searches",
    description: "Upgrade description",
  },
});

const SearchPopover = ({
  changeQuery,
  defaultQuery,
  isOwner,
  leadGenPlan,
  onBeforeSearch,
  onSearchSuccess,
  query,
  showQueryWarning,
  startLeadFinderSearch,
  viewport,
}: Props) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const onSearch = useCallback(
    (value: string) => {
      startLeadFinderSearch({ onBeforeSearch, onSearchSuccess, search: value, viewport });
    },
    [onBeforeSearch, onSearchSuccess, startLeadFinderSearch, viewport]
  );

  const handleSearch = useCallback(
    (value: string) => {
      startLeadFinderSearch({
        onBeforeSearch,
        onLimitThresholdReached: (limits) => {
          showLeadFinderLimitAlert(intl, limits, isOwner, onSearch, value);
        },
        onSearchSuccess,
        search: value,
        viewport,
      });
      analytics.completed(["Find Leads button"]);
    },
    [
      analytics,
      intl,
      isOwner,
      onBeforeSearch,
      onSearch,
      onSearchSuccess,
      startLeadFinderSearch,
      viewport,
    ]
  );

  const handleUpgradeClick = useCallback(() => {
    showLeadFinderUpsell(leadGenPlan, intl, isOwner);
  }, [intl, isOwner, leadGenPlan]);

  return (
    <ExtendedAnalytics keyPath="Lead Finder Popover">
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          <span className={styles.title}>{intl.formatMessage(messages.title)}</span>
          {(leadGenPlan === LeadFinderSubscriptionPlan.ADVANCED ||
            leadGenPlan === LeadFinderSubscriptionPlan.STARTER) && (
            <Button
              className={styles.upsell}
              leftIcon={<FontAwesomeIcon icon={faCircleInfo} size="xs" />}
              onClick={handleUpgradeClick}
            >
              {intl.formatMessage(messages.upgrade)}
            </Button>
          )}
        </div>
        <SearchBox onSearch={handleSearch} query={changeQuery ? query : defaultQuery} />
        {showQueryWarning && (
          <Alert
            className={styles.alert}
            message={intl.formatMessage(messages.queryWarning, {
              b: (text: ReactNode) => <b>{text}</b>,
            })}
            showIcon
            type="warning"
          />
        )}
        <SearchArea />
        <History
          onBeforeSearch={onBeforeSearch}
          onSearchSuccess={onSearchSuccess}
          viewport={viewport}
        />
      </div>
    </ExtendedAnalytics>
  );
};

const mapStateToProps = (state: RootState) => ({
  isOwner: isCurrentUserOwner(state),
  leadGenPlan: getLeadGenPlan(state),
  query: getLeadFinderSearch(state),
});

const mapDispatchToProps = {
  startLeadFinderSearch: startLeadFinderSearch.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchPopover);
